import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { darkTheme } from '../../lib/tokens';
import {
  adoptReviewMedia, completeReviewUpload, deleteReviewMedia, deleteReviewPost, fetchReviewMediaUrl, fetchReviewRevision,
  patchAsset, patchPost, reorderReviewMedia, requestReviewUpload, retryReviewEmail, submitReview, withdrawReview,
} from './api';
import { kanalName } from './bausteine';
import { YOUTUBE_TAGS_MAX, youtubeTagZeichen, youtubeTagsAusText } from './youtubeTags';
import type { CockpitDaten, ReviewLage, ReviewMedia, ReviewRevisionDetail } from './types';

const colors = darkTheme.colors;

/** Die Überschrift des Standes — die Lage selbst leitet der Server ab (`social.lage.ts`). */
const STAND: Record<ReviewLage['state'], string> = {
  OHNE_REVIEW: 'Ohne Christine',
  ENTWURF: 'Entwurf',
  WARTET: 'Wartet auf Christine',
  FREIGEGEBEN: '👍 Freigegeben',
  AENDERUNG_ERBETEN: '👎 Änderung erbeten',
  FREIGABE_AUFGEHOBEN: 'Freigabe aufgehoben',
  ZURUECKGEZOGEN: 'Zurückgezogen',
};
type Confirm = (input: { title: string; message: string; confirmLabel: string; destructive?: boolean }) => Promise<boolean>;

export function PostReviewPanel({ post, canWrite, refresh, confirm }: {
  post: CockpitDaten;
  canWrite: boolean;
  refresh: () => void;
  confirm: Confirm;
}): JSX.Element {
  const navigate = useNavigate();
  const [caption, setCaption] = useState(post.caption ?? '');
  const [title, setTitle] = useState(post.title ?? '');
  // Als Text bearbeitet, als Liste gespeichert. Der Abgleich läuft über den Text,
  // nicht über das Array — sonst setzte jeder Refetch eine halbe Eingabe zurück.
  const tagsGespeichert = post.tags.join(', ');
  const [tagsText, setTagsText] = useState(tagsGespeichert);
  const [cover, setCover] = useState(false);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState('');
  const [previews, setPreviews] = useState<Record<string, string>>({});
  const [snapshot, setSnapshot] = useState<ReviewRevisionDetail | null>(null);
  useEffect(() => setCaption(post.caption ?? ''), [post.caption]);
  useEffect(() => setTitle(post.title ?? ''), [post.title]);
  useEffect(() => setTagsText(tagsGespeichert), [tagsGespeichert]);

  async function action(fn: () => Promise<unknown>, success: string): Promise<void> {
    setBusy(true);
    setMessage('');
    try {
      await fn();
      refresh();
      setMessage(success);
    } catch (error) {
      setMessage(error instanceof Error ? error.message : 'Aktion fehlgeschlagen.');
    } finally {
      setBusy(false);
    }
  }

  async function upload(files: FileList | null): Promise<void> {
    if (!files || files.length === 0) return;
    await action(async () => {
      for (const [index, file] of Array.from(files).entries()) {
        const kind = file.type === 'video/mp4' ? 'VIDEO' : cover && index === 0 ? 'COVER' : 'IMAGE';
        const upload = await requestReviewUpload(post.id, file, kind, post.media.length + index);
        try {
          const response = await fetch(upload.url, { method: 'PUT', headers: upload.headers, body: file });
          if (!response.ok) throw new Error(`R2-Upload fehlgeschlagen (${response.status}).`);
          await completeReviewUpload(post.id, upload.id);
        } catch (error) {
          await deleteReviewMedia(post.id, upload.id).catch(() => undefined);
          throw error;
        }
      }
    }, 'Medien hochgeladen.');
  }

  async function preview(media: ReviewMedia): Promise<void> {
    await action(async () => {
      const { url } = await fetchReviewMediaUrl(post.id, media.id);
      setPreviews((previous) => ({ ...previous, [media.id]: url }));
    }, 'Vorschau geladen.');
  }

  async function remove(media: ReviewMedia): Promise<void> {
    // Christines 👍 gilt dem Video (K4) — ein Cover zu entfernen, lässt es stehen.
    const okay = await confirm({
      title: 'Medium entfernen',
      message: media.kind === 'COVER' ? 'Das Cover gehört nicht zu Christines Freigabe; ein 👍 bleibt.' : 'Christines 👍 gilt dem Video. Ohne dieses Medium gilt es nicht mehr.',
      confirmLabel: 'Entfernen',
      destructive: true,
    });
    if (okay) await action(() => deleteReviewMedia(post.id, media.id), 'Medium entfernt.');
  }

  async function move(index: number, direction: -1 | 1): Promise<void> {
    const ids = post.media.map((media) => media.id);
    [ids[index], ids[index + direction]] = [ids[index + direction]!, ids[index]!];
    await action(() => reorderReviewMedia(post.id, ids), 'Reihenfolge gespeichert.');
  }

  /**
   * Die Kennzeichnung gilt für alle Fassungen des Beitrags. Zur Freigabe gehört sie seit K4 nicht
   * mehr (Spec §17 V3) — eine Änderung lässt Christines 👍 stehen, eine Rückfrage braucht es nicht.
   */
  async function kennzeichnen(patch: { aiLabel?: boolean; advertising?: boolean }): Promise<void> {
    await action(() => patchAsset(post.asset.id, patch), 'Kennzeichnung gespeichert.');
  }

  async function withdraw(): Promise<void> {
    const okay = await confirm({ title: 'Einreichung zurückziehen', message: 'Christine kann diese Fassung danach nicht mehr beantworten.', confirmLabel: 'Zurückziehen', destructive: true });
    if (okay) await action(() => withdrawReview(post.id), 'Einreichung zurückgezogen.');
  }

  async function removePost(): Promise<void> {
    const okay = await confirm({ title: 'Review-Beitrag löschen', message: 'Der Review und seine Medien werden entfernt. Ein bereits auf einer Plattform veröffentlichter Beitrag bleibt dort bestehen.', confirmLabel: 'Beitrag löschen', destructive: true });
    if (!okay) return;
    await action(async () => { await deleteReviewPost(post.id); navigate('/social'); }, 'Beitrag gelöscht.');
  }

  const review = post.review;
  // Eine Fassung ohne Christine (K2, CUR-2291) wird hier bearbeitet, aber nie eingereicht.
  const mitReview = post.reviewRequired;
  const editable = canWrite && post.status !== 'GEPOSTET';
  // Der Stand kommt vom Server (K4): eigene Einreichung oder übernommen von einer Fassung mit demselben Video.
  const freigabe = post.freigabe;
  const ueber = freigabe.geerbtVon ? ` · über die ${kanalName(freigabe.geerbtVon)}-Fassung` : '';
  const state = `${STAND[freigabe.state]}${ueber}`;
  // Liegt das Video Christine über eine andere Fassung vor, geht es nicht ein zweites Mal zu ihr.
  const urteilUebernommen = Boolean(freigabe.geerbtVon) && (freigabe.state === 'FREIGEGEBEN' || freigabe.state === 'WARTET');
  const anmerkung = post.freigabeAnmerkung ?? (freigabe.geerbtVon ? freigabe.comment : review?.comment ?? null);
  const kennzeichnungOffen = post.asset.aiLabel === null;
  const canSubmit = Boolean(editable && !busy && !urteilUebernommen && post.caption && post.media.some((m) => m.complete && m.kind !== 'COVER') && post.media.every((m) => m.complete));
  // Eine Fassung ohne eigene Medien kann die Datei einer anderen übernehmen (CUR-2249).
  const quellen = post.media.length === 0 ? post.geschwister.filter((g) => g.mediaComplete) : [];
  const tagListe = youtubeTagsAusText(tagsText);
  const tagZeichen = youtubeTagZeichen(tagListe);
  const tagsZuLang = tagZeichen > YOUTUBE_TAGS_MAX;

  return <section className="mb-5 rounded-lg border p-4" style={{ borderColor: colors.border, backgroundColor: colors.bgSurface }} aria-label={mitReview ? 'Christines Review' : 'Fassung bearbeiten'}>
    {mitReview ? <>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 className="text-lg font-semibold" style={{ color: colors.textPrimary }}>Freigabe durch Christine</h2>
        <strong style={{ color: freigabe.state === 'FREIGEGEBEN' && !post.reviewBlockReason ? colors.success : colors.warning }}>{state}</strong>
      </div>
      <p className="mt-1 text-xs" style={{ color: colors.textTertiary }}>Christines Freigabe gilt dem Video (bzw. den Bildern). Titel, Caption, Tags und Kennzeichnung kannst du danach ändern; ein anderes Video braucht ein neues 👍.</p>
      {post.reviewBlockReason && <p className="mt-2 text-sm" style={{ color: colors.warning }}>Veröffentlichung gesperrt: {post.reviewBlockReason}</p>}
      {anmerkung && <p className="mt-3 whitespace-pre-wrap rounded-md p-3 text-sm" style={{ backgroundColor: colors.bgElevated, color: colors.textPrimary }}>Christines Anmerkung: {anmerkung}</p>}
      {review?.notificationError && <p className="mt-2 text-sm" style={{ color: colors.danger }}>E-Mail fehlgeschlagen: {review.notificationError}</p>}
      {review && <p className="mt-2 text-xs" style={{ color: colors.textTertiary }}>Fassung {review.number} · eingereicht {new Date(review.submittedAt).toLocaleString('de-DE')}{review.notificationSentAt ? ' · E-Mail versendet' : ''}</p>}
    </> : <h2 className="text-lg font-semibold" style={{ color: colors.textPrimary }}>Fassung bearbeiten</h2>}

    <label className="mt-5 block text-sm font-semibold" style={{ color: colors.textPrimary }}>Titel dieser Fassung
      <input value={title} onChange={(event) => setTitle(event.target.value)} readOnly={!editable} maxLength={300}
        className="mt-2 w-full rounded-md border p-3 text-sm" style={{ backgroundColor: colors.bgElevated, borderColor: colors.border, color: colors.textPrimary }} />
    </label>
    {editable && <button type="button" disabled={busy || title.trim() === (post.title ?? '')} onClick={() => void action(() => patchPost(post.id, { title: title.trim() || null }), 'Titel gespeichert.')}
      className="mt-2 min-h-[44px] rounded-lg px-3 text-sm font-semibold disabled:opacity-50" style={{ backgroundColor: colors.bgElevated, color: colors.textPrimary }}>Titel speichern</button>}

    {/* Nur YouTube kennt Tags; sie kommen aus dem Upload-Paket (CUR-2295). Christine sieht sie, freigegeben wird das Video (K4). */}
    {post.channel === 'YOUTUBE' && <>
      <label className="mt-5 block text-sm font-semibold" style={{ color: colors.textPrimary }}>Tags (YouTube, mit Komma getrennt)
        {/* Textfeld statt Eingabezeile: Ein einzeiliges Feld verschluckt die Zeilenumbrüche einer
            eingefügten Liste, aus „Diabetes⏎Insulin" würde ein einziger Tag „DiabetesInsulin". */}
        <textarea value={tagsText} onChange={(event) => setTagsText(event.target.value)} readOnly={!editable} rows={2} aria-describedby={`tags-zaehler-${post.id}`}
          className="mt-2 w-full rounded-md border p-3 text-sm" style={{ backgroundColor: colors.bgElevated, borderColor: tagsZuLang ? colors.danger : colors.border, color: colors.textPrimary }} />
      </label>
      <p id={`tags-zaehler-${post.id}`} className="mt-1 text-xs" style={{ color: tagsZuLang ? colors.danger : colors.textTertiary }}>
        {tagListe.length} {tagListe.length === 1 ? 'Tag' : 'Tags'} · {tagZeichen} von {YOUTUBE_TAGS_MAX} Zeichen (YouTube zählt Kommas und Anführungszeichen mit)
      </p>
      {editable && <button type="button" disabled={busy || tagsZuLang || tagListe.join(', ') === tagsGespeichert} onClick={() => void action(() => patchPost(post.id, { tags: tagListe }), 'Tags gespeichert.')}
        className="mt-2 min-h-[44px] rounded-lg px-3 text-sm font-semibold disabled:opacity-50" style={{ backgroundColor: colors.bgElevated, color: colors.textPrimary }}>Tags speichern</button>}
    </>}

    <label className="mt-5 block text-sm font-semibold" style={{ color: colors.textPrimary }}>Veröffentlichungsfertige Caption / Beschreibung
      <textarea value={caption} onChange={(event) => setCaption(event.target.value)} rows={8} readOnly={!editable} className="mt-2 w-full rounded-md border p-3 font-mono text-sm" style={{ backgroundColor: colors.bgElevated, borderColor: colors.border, color: colors.textPrimary }} />
    </label>
    {editable && <button type="button" disabled={busy || !caption.trim() || caption === (post.caption ?? '')} onClick={() => void action(() => patchPost(post.id, { caption: caption.trim() }), 'Caption gespeichert.')}
      className="mt-2 min-h-[44px] rounded-lg px-3 text-sm font-semibold disabled:opacity-50" style={{ backgroundColor: colors.bgElevated, color: colors.textPrimary }}>Caption speichern</button>}

    <fieldset className="mt-5" disabled={!editable || busy}>
      <legend className="text-sm font-semibold" style={{ color: colors.textPrimary }}>Kennzeichnung · gilt für alle Fassungen dieses Beitrags</legend>
      <div className="mt-2 flex flex-wrap items-center gap-4 text-sm" style={{ color: colors.textSecondary }} role="radiogroup" aria-label="KI-generierte Inhalte">
        <span>KI-generierte Inhalte:</span>
        <label className="flex min-h-[44px] items-center gap-2"><input type="radio" name={`ki-${post.id}`} checked={post.asset.aiLabel === true} onChange={() => void kennzeichnen({ aiLabel: true })} /> ja</label>
        <label className="flex min-h-[44px] items-center gap-2"><input type="radio" name={`ki-${post.id}`} checked={post.asset.aiLabel === false} onChange={() => void kennzeichnen({ aiLabel: false })} /> nein</label>
      </div>
      <label className="flex min-h-[44px] items-center gap-2 text-sm" style={{ color: colors.textSecondary }}>
        <input type="checkbox" checked={post.asset.advertising === true} onChange={(event) => void kennzeichnen({ advertising: event.target.checked })} /> Werbung / bezahlte Partnerschaft
      </label>
      {kennzeichnungOffen && <p className="text-sm" style={{ color: colors.warning }}>Vor dem Veröffentlichen angeben, ob der Beitrag KI-generierte Inhalte enthält.</p>}
    </fieldset>

    <h3 className="mt-5 font-semibold" style={{ color: colors.textPrimary }}>Medien in Veröffentlichungsreihenfolge</h3>
    <ul className="mt-2 space-y-2">
      {post.media.map((media, index) => <li key={media.id} className="rounded-md border p-3 text-sm" style={{ borderColor: colors.borderSubtle, color: colors.textSecondary }}>
        <div className="flex flex-wrap items-center gap-2">
          <span>{index + 1}. {media.kind === 'VIDEO' ? 'Video' : media.kind === 'COVER' ? 'Cover' : 'Bild'} · {(media.bytes / 1e6).toFixed(1)} MB {media.complete ? '' : '· Upload offen'}</span>
          {media.complete && <button type="button" onClick={() => void preview(media)} className="underline" style={{ color: colors.accentLight }}>Ansehen</button>}
          {editable && <button type="button" onClick={() => void remove(media)} className="underline" style={{ color: colors.danger }}>Entfernen</button>}
          {editable && index > 0 && <button type="button" disabled={busy} onClick={() => void move(index, -1)} className="underline" style={{ color: colors.accentLight }}>Nach oben</button>}
          {editable && index < post.media.length - 1 && <button type="button" disabled={busy} onClick={() => void move(index, 1)} className="underline" style={{ color: colors.accentLight }}>Nach unten</button>}
        </div>
        {previews[media.id] && (media.kind === 'VIDEO'
          ? <video className="mt-3 max-h-[360px] w-full rounded-md" controls src={previews[media.id]} />
          : <img className="mt-3 max-h-[360px] rounded-md object-contain" src={previews[media.id]} alt={`Vorschau Medium ${index + 1}`} />)}
      </li>)}
    </ul>
    {editable && quellen.length > 0 && <div className="mt-4 rounded-md border p-3 text-sm" style={{ borderColor: colors.borderSubtle, color: colors.textSecondary }}>
      <p>Dieselbe Datei wie eine andere Fassung? Übernehmen statt neu hochladen.</p>
      <div className="mt-2 flex flex-wrap gap-2">
        {quellen.map((g) => <button key={g.id} type="button" disabled={busy} onClick={() => void action(() => adoptReviewMedia(post.id, g.id), `Medien von ${kanalName(g.channel)} übernommen.`)}
          className="min-h-[44px] rounded-lg px-3 font-semibold" style={{ backgroundColor: colors.bgElevated, color: colors.textPrimary }}>
          Von {kanalName(g.channel)} übernehmen ({g.mediaCount} {g.mediaCount === 1 ? 'Medium' : 'Medien'})
        </button>)}
      </div>
    </div>}
    {editable && <div className="mt-4 space-y-3">
      <label className="flex items-center gap-2 text-sm" style={{ color: colors.textSecondary }}><input type="checkbox" checked={cover} onChange={(event) => setCover(event.target.checked)} /> Erstes gewähltes Bild ist ein Cover</label>
      <label className="block text-sm font-semibold" style={{ color: colors.textPrimary }}>Medien hochladen (MP4, JPG, PNG, WebP)
        <input type="file" accept="video/mp4,image/jpeg,image/png,image/webp" multiple disabled={busy} onChange={(event) => { void upload(event.target.files); event.target.value = ''; }} className="mt-2 block w-full text-sm" />
      </label>
      {mitReview && urteilUebernommen && <p className="text-sm" style={{ color: colors.textSecondary }}>
        Christines Urteil über dieses Video gilt auch hier{ueber} — einreichen ist nicht nötig.
      </p>}
      {mitReview && <div className="flex flex-wrap gap-2">
        <button type="button" disabled={!canSubmit} onClick={() => void action(() => submitReview(post.id), 'Fassung zur Prüfung eingereicht.')}
          className="min-h-[44px] rounded-lg px-4 font-semibold text-white disabled:opacity-50" style={{ backgroundColor: colors.accent }}>Zur Prüfung einreichen</button>
        {review && !review.withdrawnAt && <button type="button" disabled={busy} onClick={() => void action(() => retryReviewEmail(post.id), 'Zugangslink per E-Mail gesendet.')} className="min-h-[44px] rounded-lg px-3" style={{ backgroundColor: colors.bgElevated, color: colors.textPrimary }}>{review.notificationError ? 'E-Mail erneut senden' : 'Neuen Zugangslink senden'}</button>}
        {review && !review.withdrawnAt && <button type="button" disabled={busy} onClick={() => void withdraw()} className="min-h-[44px] rounded-lg px-3" style={{ backgroundColor: colors.bgElevated, color: colors.textPrimary }}>Zurückziehen</button>}
      </div>}
    </div>}
    {canWrite && <button type="button" disabled={busy} onClick={() => void removePost()} className="mt-4 min-h-[44px] rounded-lg px-3 text-sm" style={{ color: colors.danger }}>Beitrag löschen</button>}
    {mitReview && post.reviewHistory.length > 0 && <div className="mt-6 border-t pt-4" style={{ borderColor: colors.borderSubtle }}>
      <h3 className="font-semibold" style={{ color: colors.textPrimary }}>Eingereichte Fassungen</h3>
      <ul className="mt-2 space-y-2">
        {post.reviewHistory.map((revision) => <li key={revision.id} className="flex flex-wrap items-center gap-2 text-sm" style={{ color: colors.textSecondary }}>
          <span>Fassung {revision.number} · {revision.withdrawnAt ? 'zurückgezogen' : revision.state === 'APPROVED' ? '👍 freigegeben' : revision.state === 'CHANGES_REQUESTED' ? '👎 Änderung erbeten' : 'offen'} · {new Date(revision.submittedAt).toLocaleString('de-DE')}</span>
          <button type="button" onClick={() => void action(async () => setSnapshot(await fetchReviewRevision(post.id, revision.id)), 'Eingereichte Fassung geladen.')} className="underline" style={{ color: colors.accentLight }}>Fassung ansehen</button>
        </li>)}
      </ul>
      {snapshot && <div className="mt-4 rounded-md border p-3" style={{ borderColor: colors.borderSubtle }}>
        <h4 className="font-semibold" style={{ color: colors.textPrimary }}>Fassung {snapshot.number} · eingereichter Wortlaut</h4>
        {snapshot.title && <p className="mt-3 text-sm font-semibold" style={{ color: colors.textPrimary }}>{snapshot.title}</p>}
        {snapshot.tags && snapshot.tags.length > 0 && <p className="mt-2 text-xs" style={{ color: colors.textTertiary }}>Tags: {snapshot.tags.join(', ')}</p>}
        <p className="mt-3 whitespace-pre-wrap text-sm" style={{ color: colors.textSecondary }}>{snapshot.caption}</p>
        {snapshot.media.map((media, index) => media.kind === 'VIDEO'
          ? <video key={media.id} controls className="mt-3 max-h-[360px] w-full" src={media.url} aria-label={`Video der Fassung ${snapshot.number}`} />
          : <img key={media.id} className="mt-3 max-h-[360px] w-full object-contain" src={media.url} alt={`${media.kind === 'COVER' ? 'Cover' : 'Bild'} ${index + 1} der Fassung ${snapshot.number}`} />)}
        {snapshot.comment && <p className="mt-3 whitespace-pre-wrap text-sm" style={{ color: colors.textSecondary }}>Christines Anmerkung: {snapshot.comment}</p>}
      </div>}
    </div>}
    {message && <p role="status" className="mt-3 text-sm" style={{ color: colors.textSecondary }}>{message}</p>}
  </section>;
}
